import {
  HourSerieCalculatorResult,
  InputHourSerieType,
} from '../building-energy-model/hour-series-calculator'
import {
  BuildingEnergyModelFragment,
  EnergySystemFragment,
  EnergySystemType,
  HeatLoadParameters,
  ScheduleFragment,
} from '../generated/graphql'
import { calculateHourSerieFactors } from '../schedule/hour-series-factors-calculator'
import { hoursInYear } from '../util/hours-in-year'

export const heatLoadCalculator = (
  schedules: ScheduleFragment[],
  outsideTempHourSerie: number[],
  energySystem: EnergySystemFragment,
  energyModelArea: number,
  year: number,
): HourSerieCalculatorResult => {
  const params = energySystem.parameters as HeatLoadParameters

  const occupantSchedule = schedules.find(
    (s) => s.id === params.occupantScheduleId,
  )
  if (!occupantSchedule)
    throw new Error('Schedule for occupants heat loads not found')

  const occupantSerie = calculateHourSerieFactors(occupantSchedule, year)

  const applianceSchedule = schedules.find(
    (s) => s.id === params.applianceScheduleId,
  )
  if (!applianceSchedule)
    throw new Error('Schedule for appliance heat loads not found')

  const applianceSerie = calculateHourSerieFactors(applianceSchedule, year)

  const lightingSchedule = schedules.find(
    (s) => s.id === params.lightingScheduleId,
  )
  if (!lightingSchedule)
    throw new Error('Schedule for lighting heat loads not found')

  const lightingSerie = calculateHourSerieFactors(lightingSchedule, year)

  const heatLoadSerie = Array.from(Array(hoursInYear).fill(0)) as number[]

  for (let i = 0; i < outsideTempHourSerie.length; i++) {
    const currTemp = outsideTempHourSerie[i]

    const utilFactorCorrection = Math.min(
      1,
      Math.max(
        0,
        (params.affectingSpaceHeatingStartTemp - currTemp) /
          (params.affectingSpaceHeatingStartTemp -
            params.heatLoadUtilStartTemp),
      ),
    )

    occupantSerie[i] *=
      (params.occupantHeatLoad *
        energyModelArea *
        params.occupantHeatLoadUtilFactor) /
      1000

    applianceSerie[i] *= (params.applianceHeatLoad * energyModelArea) / 1000

    lightingSerie[i] *= (params.lightingHeatLoad * energyModelArea) / 1000

    heatLoadSerie[i] =
      (occupantSerie[i] +
        applianceSerie[i] * params.applianceHeatLoadUtilFactor +
        lightingSerie[i] * params.lightingHeatLoadUtilFactor) *
      utilFactorCorrection
  }

  const result: HourSerieCalculatorResult = {
    outputs: [],
    inputs: [
      {
        sourceSystemType: EnergySystemType.Cooling,
        targetSystemType: EnergySystemType.ElectricityConsumption,
        inputSerieType: InputHourSerieType.HeatLoadAppliancesElectricityDemand,
        hourSerie: applianceSerie,
      },
      {
        sourceSystemType: EnergySystemType.Cooling,
        targetSystemType: EnergySystemType.ElectricityConsumption,
        inputSerieType: InputHourSerieType.HeatLoadLightingElectricityDemand,
        hourSerie: lightingSerie,
      },
      {
        sourceSystemType: EnergySystemType.Cooling,
        targetSystemType: EnergySystemType.SpaceHeating,
        inputSerieType: InputHourSerieType.HeatLoadHeatloadSupply,
        hourSerie: heatLoadSerie,
      },
    ],
  }

  return result
}

export const calcHeatLoadHourSeries = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  area: number,
  year: number,
): HourSerieCalculatorResult => {
  let heatloadResult: HourSerieCalculatorResult = { outputs: [], inputs: [] }

  const heatLoads = model.energySystems.find(
    (system) => system.type === EnergySystemType.HeatLoad,
  )
  if (heatLoads)
    heatloadResult = heatLoadCalculator(
      model.schedules,
      outsideTempHourSerie,
      heatLoads,
      area,
      year,
    )

  return heatloadResult
}
